import React, { type KeyboardEvent, type ReactElement, useState } from 'react';
import { Logger } from '@saviynt/common';
import {
  Badge,
  Box,
  Button,
  ButtonIcon,
  ButtonSelect,
  FooterActionBar,
  Icon,
  InputField,
  Menu,
  ToggleButton,
  ToggleButtonGroup,
} from '@saviynt/design-system';
import cx from 'classnames';

import EntitlementViolationsTab from '../../features/Details/components/EntitlementDetails/ViolationsTab/EntitlementViolationsTab';
import { SodHeader } from '../../shared/components';
import { CF_NEU_STL, testIds } from '../../shared/constants';
import { useInvertedLocalize, useTypedSelector } from '../../shared/hooks';
import { type RootState } from '../../shared/types';

import './Recommendation.css';

/**
 * Layout for the Recommendation page, can and should be used as a starting point for all new pages.
 * Consider renameing the route to something else as needed.
 *
 * TODO:
 * - All onClick Loggers should be updated
 * - ApplicationIcon needs to be updated after MR-!9044
 * - All the text on this page will need Localization after dynamic rendering
 * - Inigration tests need to be added, simply target the placeholder buttons and maybe the history.push
 * - initialListOptions should come from an API call
 * - AAG_SOD_RECOMMENDATIONS should be a dynamic route: ECMv6/soddetail/violations/recommendations?id='<recommendation id>
 */
function Recommendation(): ReactElement {
  const l = useInvertedLocalize();
  const { dataTheme } = useTypedSelector(
    (state: RootState) => state.aag.global
  );
  const [selection, setSelection] = useState(1);
  const pageClasses = cx('Sod-Recommendation');
  const trigger = (
    <ButtonIcon
      size='medium'
      kind='ghost'
      ariaLabel={l('more')}
      icon={<Icon kind='MoreVertical' color='neutral-100' size='medium' />}
    />
  );

  return (
    <div
      data-theme={dataTheme}
      className={pageClasses}
      data-testid={testIds.SOD_RECOMMENDATION_PAGE}>
      <SodHeader
        title='Disable dormant accounts to remediate 54 associated violations.'
        description='Review the result of the remediation simulation, then apply the
            change or save to simulation workbench for later.'
        applicationIconKind='SaviCompanion'
        className='Sod-Recommendation-header'
        breadcrumbItems={[
          { text: l('Violations'), href: '/violations' },
          { text: l('Recommendations'), href: '/recommendations' },
        ]}
      />
      {/* Main list section */}
      <div className='Sod-Recommendation-list-section'>
        <div className='Sod-Recommendation-list-section-tab-group'>
          <ToggleButtonGroup selection={selection} setSelection={setSelection}>
            <ToggleButton
              icon={<Icon kind='Account' />}
              label={l('Account')}
              size='medium'
              BadgeComp={
                <Badge
                  kind='number'
                  size='medium'
                  badgeStyle='outlined'
                  number={23}
                  color='info'
                />
              }
            />
            <ToggleButton
              icon={<Icon kind='SOD' />}
              label={l('Violation')}
              size='medium'
              BadgeComp={
                <Badge
                  kind='number'
                  size='medium'
                  badgeStyle='outlined'
                  number={54}
                  color='info'
                />
              }
            />
          </ToggleButtonGroup>
        </div>
        {selection === 1 && (
          <div className='Sod-Recommendation-list-section-lists-header'>
            <div className='Sod-Recommendation-list-section-lists-header-count'>
              {l('23 Items')}
            </div>
            <div className='Sod-Recommendation-list-section-lists-header-view-controls'>
              <Box
                tag='div'
                className=''
                tabIndex={0}
                onKeyDown={(e: KeyboardEvent<HTMLDivElement>) => {
                  if (e.key === 'Enter') {
                    //
                  }
                }}>
                <InputField
                  name='violation-name-search'
                  placeholder={l('Search')}
                  kind='searchCollapse'
                  value=''
                  size='medium'
                  prefixIcon={<Icon kind='Search' color={CF_NEU_STL} />}
                  shouldDisallowBrackets
                  dataTestId={testIds.SOD_CONTROLS_SEARCH_BOX}
                  isDisabled
                />
              </Box>
              <Box tag='div' className=''>
                <ButtonSelect
                  kind='rectangle'
                  label={l('Filter')}
                  onClick={() => {}}
                  isOpen={false}
                  size='medium'
                  prefixIcon={<Icon kind='Filter' />}
                  isVisualCueEnabled={false}
                />
              </Box>
            </div>
          </div>
        )}
        <div className='Sod-Recommendation-list-section-lists-data'>
          {selection === 1 && l('This is account data.')}
          {/* Accounts */}
          {selection === 2 && <EntitlementViolationsTab />}
        </div>
      </div>
      {/* Footer */}
      <FooterActionBar className='Sod-Recommendation-footerActionBar'>
        <Button
          type='button'
          kind='subtle'
          onClick={() => Logger.log('Hi Cancel, How are you?')}>
          {l('Cancel')}
        </Button>
        <Box className='Sod-Recommendation-footerGroupRight'>
          <Button
            type='button'
            kind='outlined'
            onClick={() => Logger.log('Hi Save for Later, How are you?')}>
            {l('Save to Simulation')}
          </Button>
          <Button
            type='button'
            kind='filled'
            onClick={() => Logger.log('Hi Apply, How are you?')}>
            {l('Revoke (23)')}
          </Button>
          <Box className='Example-Menu-flexBoxSampleClass'>
            <Menu
              options={[
                {
                  label: l('Generate Report'),
                  value: 'generate_report',
                  icon: 'Report',
                },
              ]}
              onChange={() => {}}
              trigger={trigger}
            />
          </Box>
        </Box>
      </FooterActionBar>
    </div>
  );
}

export default Recommendation;
